import { Button, Typography } from "@mui/material";
import { omit } from "lodash-es";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import ListViewLayout from "@/App/Layouts/ListViewLayout";
import SimpleViewPageHeader from "@/App/Layouts/PageHeader/SimpleViewPageHeader";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedMenuItem from "@/common/components/Auth/AuthorizedMenuItem";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataExportModal from "@/common/components/DataExport/DataExportModal";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { TagStaticFilterInputDto, TagsHelper } from "@/common/helpers/entity/tags";
import { getTypedPath } from "@/common/helpers/typedPath";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCommonRequestParams } from "@/common/hooks/api/useCommonRequestParams";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { PropagatedDeps } from "@/common/hooks/render/usePropagatedDeps";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  ExportType,
  FilterDefinitionDto,
  GeneralAttachedTagsInputDto,
  TenantRequestDto,
  TenantRequestGetPaginatedDto,
} from "@/core/api/generated/v1";

import BaseEntityListView from "../../components/BaseEntityListView";
import TenantRequestTabular, {
  TenantRequestGetPaginatedInputDto,
  TenantRequestTabularTabs,
} from "./TenantRequestTabular";

const defaultDisplayProps = {
  breadcrumbs: true,
  counters: true,
  header: true,
  create: true,
  edit: true,
  delete: true,
  viewVariant: ViewVariant.Page,
};

const defaultPropagatedDeps = {
  refetch: {},
};
export type TenantRequestPaginatedListPropagatedDepsMap = typeof defaultPropagatedDeps;

export interface TenantRequestPaginatedListOwnProps {
  displayProps?: Partial<typeof defaultDisplayProps>;
  initialValues?: {
    filterDefinitionDto?: Nil<FilterDefinitionDto>;
  };
  defaultValues?: {
    limit?: number;
  } & Partial<Omit<TenantRequestGetPaginatedDto, "tags">>;
  propagatedDeps?: PropagatedDeps<TenantRequestPaginatedListPropagatedDepsMap>;
}

export type TenantRequestPaginatedListProps = TenantRequestPaginatedListOwnProps;

export default function TenantRequestPaginatedList({
  displayProps,
  initialValues,
  defaultValues,
  propagatedDeps,
}: TenantRequestPaginatedListProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const currentTenant = useCurrentTenant();

  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityListView(EntityType.TenantRequest),
      viewVariant: displayProps.viewVariant,
    },
  });

  const commonRequestParams = useCommonRequestParams<TenantRequestGetPaginatedInputDto>({
    statePersistence: {
      persistenceKey: EntityType.TenantRequest,
      isEnabled:
        !displayProps?.viewVariant ||
        [ViewVariant.Page, ViewVariant.Modal].includes(displayProps.viewVariant),
    },
    initialValues: {
      filterDefinitionDto: initialValues?.filterDefinitionDto || undefined,
    },
    defaultValues: {
      limit: defaultValues?.limit,
      params: {
        ...defaultValues,
      },
    },
    initParams: (filterDefinition) => {
      const tagFilter = filterDefinition.filterDefinition?.items.find(
        (x) => x.field === getTypedPath<TenantRequestDto>().tags.$path,
      );

      return {
        tags: {
          operator: tagFilter?.operator,
          query: tagFilter?.value as GeneralAttachedTagsInputDto,
        } as TagStaticFilterInputDto,
      };
    },
    options: { useUrlSearchParamsToStoreSelectedPage: true },
  });

  const [isDataExportModalOpened, setIsDataExportModalOpened] = useState<boolean>(false);

  const countersRequest = useApiRequest(
    apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsCountersGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
    },
    { skip: !displayProps?.counters },
  );
  const counters = countersRequest?.data;

  const paginatedTenantRequestsRequest = useApiRequest(
    apiClient.tenantRequestsApi.apiV1TenantToTenantTenantRequestsGetPost,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      tenantRequestGetPaginatedDto: {
        ...omit(commonRequestParams.params, "tags"),
        offset: commonRequestParams.offset,
        limit: commonRequestParams.limit,
        search: commonRequestParams.search,
        sortDefinition: commonRequestParams.sortDefinitionDto,
        filterDefinition: commonRequestParams.filterDefinitionDto,
        tags:
          commonRequestParams.filterDefinition?.items
            .filter((x) => x.field === getTypedPath<TenantRequestDto>().tags.$path)
            .map((x) => TagsHelper.mapFromFilter(x))[0] || undefined,
        stage:
          commonViewParams.activeTabId === TenantRequestTabularTabs.All
            ? undefined
            : enumService.isEnumValue(ApiEnumName.TenantRequestStage, commonViewParams.activeTabId)
              ? commonViewParams.activeTabId
              : undefined,
      },
    },
    {
      deps: [
        ...commonRequestParams.deps,
        commonRequestParams.filterDefinition,
        commonViewParams.activeTabId,
        propagatedDeps?.depsMap["refetch"],
      ],
      debouncedDeps: {
        deps: [...commonRequestParams.debouncedDeps],
        wait: 500,
        options: { leading: false, trailing: true },
      },
      commonRequestParams: commonRequestParams,
    },
  );
  const paginatedTenantRequests = paginatedTenantRequestsRequest?.data;
  const tenantRequestIds = useMemo(
    () => paginatedTenantRequests?.items?.map((item) => item.id!) || [],
    [paginatedTenantRequests?.items],
  );

  useRealtimeDataUpdates<TenantRequestDto>({
    channelNames: [
      DataUpdatesChannelName.Entities(undefined, EntityType.TenantRequest),
      DataUpdatesChannelName.Entities(currentTenant?.id, EntityType.TenantRequest),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      countersRequest.refetch();
      paginatedTenantRequestsRequest.handleEntityChanged2({
        dto: data,
        // cover issue when tags are null here as tenant-local data is not sent via SignalR
        getUpdatedEntity: (params) => ({
          ...params.oldEntity,
          ...params.newEntity,
          tags: params.newEntity.tags || params.oldEntity.tags,
        }),
      });
    },
  });

  return (
    <BaseEntityListView entityType={EntityType.TenantRequest} isIniting={!currentTenant}>
      <ListViewLayout
        displayProps={displayProps}
        header={
          displayProps?.header && (
            <SimpleViewPageHeader
              title='Requests'
              primaryActions={
                displayProps?.create && (
                  <AuthorizedElement permissionsAny={[AppPermission.TenantRequestManage]}>
                    <Button
                      variant='contained'
                      color='primary'
                      startIcon={<AppIcon of='add' />}
                      component={RouterLink}
                      to={ROUTE_PATH.TENANT_REQUEST_CREATE}
                    >
                      Create new request
                    </Button>
                  </AuthorizedElement>
                )
              }
              secondaryActions={
                <AuthorizedElement
                  permissionsAny={[AppPermission.ImportManage, AppPermission.ExportManage]}
                >
                  <MenuWithTrigger
                    withAuthCloseOnClick
                    trigger={
                      <AppIconButton>
                        <AppIcon of='moreVert' />
                      </AppIconButton>
                    }
                  >
                    <AuthorizedMenuItem
                      permissions={[AppPermission.ExportManage]}
                      onClick={() => setIsDataExportModalOpened(true)}
                    >
                      <AppIcon of='export' fontSize='small' sx={{ transform: "rotate(180deg)" }} />
                      <Typography sx={{ ml: 1 }}>Export C2C requests</Typography>
                    </AuthorizedMenuItem>
                  </MenuWithTrigger>
                </AuthorizedElement>
              }
            />
          )
        }
      >
        <TenantRequestTabular
          commonViewParams={commonViewParams}
          commonRequestParams={commonRequestParams}
          paginatedTenantRequestsRequest={paginatedTenantRequestsRequest}
          paginatedTenantRequests={paginatedTenantRequests}
          counters={counters}
          displayProps={displayProps}
        />
      </ListViewLayout>

      {/* Export data modal */}
      <DataExportModal
        open={isDataExportModalOpened}
        onClose={() => {
          setIsDataExportModalOpened(false);
        }}
        dataExportProps={{
          type: ExportType.TenantRequest,
          currentPageScope: {
            ids: tenantRequestIds,
          },
          allFilteredScope: {
            tenantRequest:
              paginatedTenantRequestsRequest.requestParams?.tenantRequestGetPaginatedDto,
          },
          onExportDone: () => {
            setIsDataExportModalOpened(false);
          },
        }}
      />
    </BaseEntityListView>
  );
}
